import { useState, useEffect, useCallback, useRef } from 'react';

// ── Server clock ─────────────────────────────────────────────────────────────
// Countdowns on the floor views ("setup ends in 1:25", "tool change resumes in 4:12") are computed
// as `phaseEndsAt - now`. `phaseEndsAt` is issued by the SERVER; `now` used to be `Date.now()` on
// whatever device happened to be rendering. Those are two different clocks, and the difference is
// invisible until it isn't:
//
//   A tablet whose clock ran ahead of the server showed `0:00` on a setup that the desktop, sitting
//   next to it on the same payload, showed as `1:25`. Same data, same arithmetic, same component
//   logic — the only free variable was the device clock, and a fast one makes every server deadline
//   look already-expired, which `Math.max(0, …)` then clamps to a convincing-looking 0:00.
//
// So the countdown must be anchored to the server's clock, not the device's. Every REST poll already
// carries the server's time in its `Date` response header, so the offset is free: no payload change,
// no extra request, no server change. The header has 1-second resolution and we split the round-trip
// in half, which is worth roughly ±(0.5s + RTT/2) — irrelevant against countdowns measured in
// minutes, and vastly better than an unbounded device-clock error.
//
// `skewMs` is exposed so a view can SAY that it is correcting a bad clock. A booth tablet with a
// wrong date is otherwise diagnosable only by noticing that the numbers are wrong.

// Only move the correction when it actually moved. The Date header's 1s granularity plus network
// jitter would otherwise wobble the offset by a second on every poll and re-render every countdown.
const SKEW_EPSILON_MS = 2000;

/** Offset between server time and this device's clock, in ms. Positive = the device is BEHIND. */
export function skewFromResponse(res, sentAt, receivedAt = Date.now()) {
  const header = res?.headers?.date || res?.headers?.Date;
  if (!header) return null;
  const serverMs = new Date(header).getTime();
  if (!Number.isFinite(serverMs)) return null;
  // Midpoint of the round trip is our best estimate of "when the server stamped it, in device time".
  return serverMs - (sentAt + receivedAt) / 2;
}

/**
 * A 1s clock that ticks in SERVER time.
 *
 * Returns `now` (ms, server-aligned), `skewMs`, and `observe(res, sentAt)` — call that with any
 * axios response and the timestamp taken immediately before the request.
 */
export function useServerClock(tickMs = 1000) {
  const [deviceNow, setDeviceNow] = useState(() => Date.now());
  const [skewMs, setSkewMs] = useState(0);
  const skewRef = useRef(0);

  useEffect(() => {
    const id = setInterval(() => setDeviceNow(Date.now()), tickMs);
    return () => clearInterval(id);
  }, [tickMs]);

  const observe = useCallback((res, sentAt) => {
    const next = skewFromResponse(res, sentAt);
    if (next == null) return;
    if (Math.abs(next - skewRef.current) < SKEW_EPSILON_MS) return;
    skewRef.current = next;
    setSkewMs(next);
  }, []);

  return { now: deviceNow + skewMs, skewMs, observe };
}

// A device clock wrong by more than this is worth telling the operator about — it is far beyond
// anything the round-trip estimate explains, so it means the device itself is set wrong.
export const SKEW_WARN_MS = 90000;

/** "4m ahead" / "12m behind" — how the DEVICE reads relative to the server. */
export function describeSkew(skewMs) {
  const behind = skewMs > 0;                    // positive skew = device behind the server
  const mins = Math.round(Math.abs(skewMs) / 60000);
  const unit = mins >= 1 ? `${mins}m` : `${Math.round(Math.abs(skewMs) / 1000)}s`;
  return `${unit} ${behind ? 'behind' : 'ahead'}`;
}
